import ProductAdd from '@blocks/product/product-add'
import ProductOptionList from '@blocks/product/product-option-list'
import ProductPrice from '@blocks/product/product-price'
import ProductThumbnail from '@blocks/product/product-thumbnail'
import { ButtonColor, ButtonVariant } from '@components/buttons/button'
import SimpleLink from '@components/simple-link'
import { isMarketingConsentGranted } from '@lib/analytics/consent'
import { AnalyticsContext } from '@lib/analytics/context'
import { getProductEventPayload } from '@lib/analytics/events'
import { AnalyticsEventName } from '@lib/analytics/types'
import { itemAnimation } from '@lib/animate'
import { CartContext } from '@lib/cart/context'
import { getPriceNumber } from '@lib/helpers'
import { LanguageContext } from '@lib/language-context'
import {
  Filter,
  getFallbackVariantFromOptions,
  getFormattedProductTitle,
  getVariantFromOptions,
} from '@lib/product'
import {
  getFilteredProductVariant,
  getProductTitle,
  getSurfacedOption,
} from '@lib/product-card'
import { CollectionProduct } from '@lib/product-collection'
import { OptionValue, getProductOptions } from '@lib/product-options'
import { getLinkPageUrl, getPageUrl } from '@lib/routes'
import { ShopContext } from '@lib/shop-context'
import { SiteContext } from '@lib/site-context'
import { StringsContext } from '@lib/strings-context'
import cx from 'classnames'
import { motion } from 'framer-motion'
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import ProductCardChips from './product-card-chips'

interface ProductCardProps {
  product: CollectionProduct
  preferredOption?: OptionValue
  filters?: Filter[]
  showThumbnails?: boolean
  showPrice?: boolean
  showOption?: boolean
  showQuickAdd?: boolean
  onClick?: () => void
  className?: string
  titleClassName?: string
}

const surfacedOptionVariantLimit = 3

const ProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
  (
    {
      product,
      preferredOption,
      filters = [],
      showThumbnails = false,
      showPrice = false,
      showOption = false,
      showQuickAdd = false,
      onClick,
      className,
      titleClassName,
    },
    ref,
  ) => {
    const { triggerAnalyticsEvent } = useContext(AnalyticsContext)
    const { openCartAfterAddingItems, addItemsToCart, toggleCart } =
      useContext(CartContext)
    const { locale } = useContext(LanguageContext)
    const { currencyCode } = useContext(ShopContext)
    const { settings } = useContext(SiteContext)
    const strings = useContext(StringsContext)

    const [activeVariant, setActiveVariant] = useState(() =>
      getFilteredProductVariant(product, filters ?? [], preferredOption),
    )
    const [title, setTitle] = useState(() =>
      getProductTitle(
        locale,
        product,
        activeVariant ?? undefined,
        preferredOption,
      ),
    )

    // Product URL
    const productUrl = useMemo(() => {
      let productSlug = product.slug.current

      // Get product slug for compacted product from mapping
      if (
        activeVariant?.variantID &&
        product.variantIdProductSlugMap?.[activeVariant.variantID]
      ) {
        productSlug = product.variantIdProductSlugMap[activeVariant.variantID]
      }

      return getLinkPageUrl('product', productSlug, {
        variant: activeVariant ? `${activeVariant.variantID}` : '',
      })
    }, [activeVariant, product])

    // Surfaced option data
    const surfacedOptionOverThumbnail = useMemo(() => {
      return getSurfacedOption(
        product.options,
        product.surfaceOptions?.overThumbnail,
      )
    }, [product.options, product.surfaceOptions])

    const surfacedOptionOverTitle = useMemo(() => {
      return getSurfacedOption(
        product.options,
        product.surfaceOptions?.overTitle,
      )
    }, [product.options, product.surfaceOptions])

    const surfacedOptionOverThumbnailProductOptions = useMemo(() => {
      if (!activeVariant || !surfacedOptionOverThumbnail) {
        return
      }

      return getProductOptions(
        surfacedOptionOverThumbnail,
        activeVariant.options,
        null,
        product.galleryPhotos ?? [],
        product.optionSettings ?? [],
        product.variants ?? [],
        preferredOption?.value,
        surfacedOptionVariantLimit,
      )
    }, [activeVariant, preferredOption, product, surfacedOptionOverThumbnail])

    const surfacedOptionOverTitleProductOptions = useMemo(() => {
      if (!activeVariant || !surfacedOptionOverTitle) {
        return
      }

      return getProductOptions(
        surfacedOptionOverTitle,
        activeVariant.options,
        null,
        product.galleryPhotos ?? [],
        product.optionSettings ?? [],
        product.variants ?? [],
        preferredOption?.value,
      )
    }, [activeVariant, preferredOption, product, surfacedOptionOverTitle])

    const isAvailabilityCountdownActive = useMemo(() => {
      if (
        !product.enableAvailabilityCountdown ||
        !product.availabilityCountdownTarget
      ) {
        return false
      }

      const date = Date.parse(product.availabilityCountdownTarget)
      const now = Date.now()

      return date > now
    }, [product])

    const showSurfacedOptionOverThumbnail =
      showOption &&
      !isAvailabilityCountdownActive &&
      !!surfacedOptionOverThumbnail &&
      !!activeVariant &&
      !!surfacedOptionOverThumbnailProductOptions
    const showSurfacedOptionOverTitle =
      showOption &&
      !isAvailabilityCountdownActive &&
      !!surfacedOptionOverTitle &&
      !!activeVariant &&
      !!surfacedOptionOverTitleProductOptions

    const updateActiveVariant = useCallback(
      (optionName: string, optionValue: string) => {
        let newVariant = activeVariant
          ? getVariantFromOptions(
              product.variants ?? [],
              activeVariant.options,
              optionName,
              optionValue,
            )
          : null

        if (!newVariant) {
          newVariant = getFallbackVariantFromOptions(
            product.variants ?? [],
            optionName,
            optionValue,
          )
        }

        if (newVariant && newVariant.variantID !== activeVariant?.variantID) {
          setActiveVariant(newVariant)
        }
      },
      [activeVariant, product, setActiveVariant],
    )

    const handleAddToCart = useCallback(async () => {
      if (!activeVariant) {
        return
      }

      const quantity = 1
      const isSuccessful = await addItemsToCart([
        {
          id: activeVariant.variantID,
          quantity,
        },
      ])

      if (isSuccessful) {
        // Trigger add to cart event
        triggerAnalyticsEvent(
          AnalyticsEventName.AddToCart,
          getProductEventPayload(
            getFormattedProductTitle(locale, product.title),
            [
              {
                variantId: activeVariant.variantID,
                options: activeVariant.options,
                price: activeVariant.price,
                comparePrice: activeVariant.comparePrice,
                quantity,
              },
            ],
            currencyCode,
          ),
        )

        // Klaviyo event
        if (settings && window.klaviyo && isMarketingConsentGranted()) {
          const domain = settings.siteURL.replace(/\/+$/g, '')
          const productUrl = getPageUrl(
            locale,
            'product',
            product.slug.current,
            {
              variant: `${activeVariant.variantID}`,
            },
          )
          const klaviyoProductItem: KlaviyoProductItem = {
            ProductName: getFormattedProductTitle(locale, product.title),
            ProductID: `${product.productID}`,
            VariantID: `${activeVariant.variantID}`,
            SKU: activeVariant.sku,
            URL: `${domain}${productUrl}`,
            Price: getPriceNumber(activeVariant.price),
          }

          if (activeVariant.comparePrice) {
            klaviyoProductItem.CompareAtPrice = getPriceNumber(
              activeVariant.comparePrice,
            )
          }

          window.klaviyo.track('Added To Cart', klaviyoProductItem)
        }

        // Triple Whale pixel event
        if (window.TriplePixel) {
          window.TriplePixel('AddToCart', {
            item: `${product.productID}`,
            q: quantity,
            v: `${activeVariant.variantID}`,
          })
        }

        if (openCartAfterAddingItems) {
          toggleCart(true)
        }
      }
    }, [
      activeVariant,
      addItemsToCart,
      currencyCode,
      locale,
      openCartAfterAddingItems,
      product,
      settings,
      toggleCart,
      triggerAnalyticsEvent,
    ])

    // Update page title when product or variant changes
    useEffect(() => {
      const newTitle = getProductTitle(
        locale,
        product,
        activeVariant ?? undefined,
        preferredOption,
      )
      setTitle(newTitle)
    }, [activeVariant, locale, preferredOption, product])

    return (
      <motion.div
        ref={ref}
        variants={itemAnimation}
        className={cx(
          'h-full flex flex-col justify-between relative group z-0',
          className,
        )}
      >
        <div className="absolute top-0 right-0 flex gap-1 z-[1]">
          <ProductCardChips product={product} />
        </div>

        {showThumbnails && !!activeVariant && (
          <div className="relative flex flex-col items-center">
            <SimpleLink href={productUrl} className="w-full">
              <ProductThumbnail
                galleryPhotos={product.galleryPhotos}
                listingPhotos={product.listingPhotos}
                activeVariant={activeVariant}
              />
            </SimpleLink>

            {showQuickAdd && !isAvailabilityCountdownActive && activeVariant.inStock && (
              <div
                className={cx(
                  'absolute bottom-0 inset-x-0 z-30 text-center mx-4 mb-8 opacity-0 invisible',
                  'translate-y-1/2 transition-all will-change-transform',
                  'group-hover:opacity-100 group-hover:visible group-hover:translate-y-0',
                )}
              >
                <ProductAdd
                  variant={ButtonVariant.FILLED}
                  color={ButtonColor.DEFAULT}
                  onClick={handleAddToCart}
                >
                  {strings.buttonAddToCart}
                </ProductAdd>
              </div>
            )}

            {showSurfacedOptionOverThumbnail && (
              <div className="absolute bottom-3 right-3 z-30 hidden group-hover:flex">
                <ProductOptionList
                  productOptions={surfacedOptionOverThumbnailProductOptions}
                  hasShowMore={
                    surfacedOptionOverThumbnail.values.length >
                    surfacedOptionVariantLimit
                  }
                  showMoreUrl={productUrl}
                  isInProductCard
                  onSelect={(optionValue) =>
                    updateActiveVariant(
                      surfacedOptionOverThumbnail.name,
                      optionValue,
                    )
                  }
                />
              </div>
            )}
          </div>
        )}

        <div
          className={cx(
            'flex flex-col sm:flex-row items-start justify-between gap-2 p-3 leading-tight uppercase',
            titleClassName,
          )}
        >
          <SimpleLink
            href={productUrl}
            onClick={onClick}
            onBeforeInput={onClick}
            tabIndex={0}
            className={cx('text-xs block no-underline text-current', {
              'group-hover:hidden': showSurfacedOptionOverTitle,
            })}
          >
            {title}
          </SimpleLink>

          {showSurfacedOptionOverTitle && (
            <ProductOptionList
              productOptions={surfacedOptionOverTitleProductOptions}
              isInProductCard
              onSelect={(optionValue) =>
                updateActiveVariant(surfacedOptionOverTitle.name, optionValue)
              }
              className="hidden group-hover:flex"
            />
          )}

          {showPrice && (
            <ProductPrice
              className="text-xs"
              price={activeVariant?.price ?? product.price}
              comparePrice={activeVariant?.comparePrice ?? product.comparePrice}
            />
          )}
        </div>
      </motion.div>
    )
  },
)

ProductCard.displayName = 'ProductCard'

export default ProductCard
